/**
 * Tasks & Stories Help Content
 *
 * Covers My Tasks, task detail, story detail, and task management.
 */

import type { HelpContent } from '../types';
import {
  createHelpContent,
  createSection,
  createFAQ,
  createTip,
  createWalkthrough,
  createRelatedLink,
} from '../utils';

// =============================================================================
// MAIN TASKS HELP
// =============================================================================

export const tasksHelp: HelpContent = createHelpContent({
  id: 'tasks',
  title: 'Tasks & Stories',
  description: 'View, create, and manage tasks and user stories',
  lastUpdated: '2026-02-08',
  version: '1.0',

  tips: [
    createTip(
      'tip-filters',
      'pro-tip',
      'Quick Filters',
      'Use the status and priority filters to quickly find the tasks that need your attention. Filter by "In Progress" to see your active work.'
    ),
    createTip(
      'tip-effort',
      'info',
      'Effort Estimation',
      'Adding effort estimates to tasks helps with planning and lets you compare planned vs actual time in analytics.'
    ),
  ],

  sections: [
    createSection(
      'overview',
      'Overview',
      `<p>The Tasks section helps you manage your work items. Tasks are individual work items that can be assigned to team members and tracked through their lifecycle.</p>
      <p>Key features:</p>
      <ul>
        <li><strong>My Tasks</strong> — Your personal task list with filters and sorting</li>
        <li><strong>Task Details</strong> — Full task information including status, priority, effort, and comments</li>
        <li><strong>Stories & Epics</strong> — Higher-level work items that group related tasks</li>
        <li><strong>Comments & Activity</strong> — Collaborate on tasks with your team</li>
      </ul>`
    ),
    createSection(
      'my-tasks',
      'My Tasks List',
      `<p>Your personal task list shows all tasks assigned to you. Features include:</p>
      <ul>
        <li><strong>Filter by status</strong> — Not Started, In Progress, Completed, On Hold</li>
        <li><strong>Filter by priority</strong> — Critical, High, Medium, Low</li>
        <li><strong>Sort options</strong> — By due date, priority, project, or recent activity</li>
        <li><strong>Quick actions</strong> — Change status directly from the list</li>
      </ul>`
    ),
    createSection(
      'task-detail',
      'Task Details',
      `<p>Each task has the following properties:</p>
      <ul>
        <li><strong>Status</strong> — Current state (Not Started, In Progress, Completed, On Hold, At Risk, Cancelled)</li>
        <li><strong>Priority</strong> — Urgency level (Critical, High, Medium, Low)</li>
        <li><strong>Assignee</strong> — Who is responsible for the task</li>
        <li><strong>Due Date</strong> — When the task should be completed</li>
        <li><strong>Effort</strong> — Estimated hours to complete</li>
        <li><strong>Description</strong> — Detailed requirements and context</li>
        <li><strong>Comments</strong> — Discussion thread for collaboration</li>
      </ul>`
    ),
    createSection(
      'stories',
      'Stories & Epics',
      `<p>Stories and epics provide structure above individual tasks:</p>
      <ul>
        <li><strong>Stories</strong> — User-facing features or requirements that may contain multiple tasks</li>
        <li><strong>Epics</strong> — Large bodies of work that span multiple stories</li>
      </ul>
      <p>Breaking work into stories and tasks helps with estimation, tracking, and team collaboration.</p>`
    ),
    createSection(
      'comments',
      'Comments & Activity',
      '<p>Use the comments section on tasks to discuss requirements, share updates, and ask questions. All team members with access to the project can see and respond to comments. Activity history tracks all changes made to the task.</p>'
    ),
  ],

  walkthroughs: [
    createWalkthrough(
      'walkthrough-create-task',
      'How to Create and Assign a Task',
      'Create a new task and assign it to a team member.',
      [
        {
          title: 'Navigate to the project',
          description: 'Go to the project where you want to create the task.',
        },
        {
          title: 'Click "Add Task"',
          description: 'Open the task creation form.',
        },
        {
          title: 'Fill in details',
          description: 'Enter the task title, description, set priority and due date.',
        },
        {
          title: 'Assign to a team member',
          description: 'Select the assignee from the project team members.',
        },
        {
          title: 'Add effort estimate',
          description: 'Set the estimated hours to help with planning.',
        },
        {
          title: 'Save',
          description: 'Save the task. The assignee will be notified.',
        },
      ],
      2
    ),
    createWalkthrough(
      'walkthrough-track',
      'How to Track Task Progress',
      'Update task status as you work through it.',
      [
        {
          title: 'Find your task',
          description: 'Go to My Tasks or find the task in the project.',
        },
        {
          title: 'Update status to In Progress',
          description: 'When you start working, change the status to "In Progress".',
        },
        {
          title: 'Log time against it',
          description: 'Use the timesheet to log hours against this task for accurate tracking.',
        },
        {
          title: 'Mark as Complete',
          description: 'When finished, change the status to "Completed".',
        },
      ],
      1
    ),
  ],

  faqs: [
    createFAQ(
      'faq-task-vs-story',
      'What\'s the difference between a task and a story?',
      'A task is a specific, actionable work item. A story is a higher-level requirement that may contain multiple tasks. Stories describe "what" needs to be done; tasks describe "how" to do it.',
      ['task', 'story', 'difference', 'what', 'epic']
    ),
    createFAQ(
      'faq-status',
      'What do the different status values mean?',
      'Not Started: work hasn\'t begun. In Progress: actively being worked on. On Hold: paused temporarily. At Risk: behind schedule or blocked. Completed: work is done. Cancelled: no longer needed.',
      ['status', 'meaning', 'values', 'states']
    ),
    createFAQ(
      'faq-effort',
      'How should I estimate effort?',
      'Estimate in hours. Consider the actual work time, not calendar time. Include research, implementation, testing, and review. It\'s better to slightly overestimate than underestimate.',
      ['effort', 'estimate', 'hours', 'how much']
    ),
    createFAQ(
      'faq-assign',
      'Can I reassign a task?',
      'Yes, edit the task and change the assignee. The new assignee will be notified. This is useful when workloads change or when specialized skills are needed.',
      ['reassign', 'change', 'assignee', 'transfer']
    ),
    createFAQ(
      'faq-due-dates',
      'What happens when a task is overdue?',
      'Overdue tasks are highlighted in the task list. The assignee and project manager receive notifications. Update the status or due date to reflect the current reality.',
      ['overdue', 'late', 'past due', 'deadline']
    ),
    createFAQ(
      'faq-bulk',
      'Can I update multiple tasks at once?',
      'Some bulk actions are available from the task list view, such as changing status for multiple selected tasks.',
      ['bulk', 'multiple', 'batch', 'many']
    ),
  ],

  relatedLinks: [
    createRelatedLink('Projects', '/projects', { description: 'View project details and all tasks' }),
    createRelatedLink('Timesheet', '/timesheet', { description: 'Log time against tasks' }),
    createRelatedLink('Analytics', '/analytics', { description: 'View effort variance and project health' }),
  ],

  keywords: [
    'tasks',
    'stories',
    'epics',
    'to-do',
    'assignments',
    'backlog',
    'progress',
    'status',
    'priority',
    'effort',
  ],
});

// =============================================================================
// SECTION-LEVEL HELP
// =============================================================================

export const taskDetailHelp: HelpContent = createHelpContent({
  id: 'tasks:detail',
  title: 'Task Details',
  description: 'View and edit a specific task',
  parentId: 'tasks',
  sections: [
    createSection('overview', 'Overview', '<p>The task detail page shows all information about a specific task. Edit any field to update the task. Changes are saved automatically and tracked in the activity log.</p>'),
  ],
  faqs: [
    createFAQ('faq-1', 'Can I see who changed a task?', 'Yes, the activity log at the bottom of the task shows all changes with timestamps and who made them.', ['history', 'changes', 'who', 'audit']),
  ],
  keywords: ['detail', 'edit', 'task', 'view'],
});

export const storyDetailHelp: HelpContent = createHelpContent({
  id: 'tasks:story',
  title: 'Story Details',
  description: 'View and manage a user story and its child tasks',
  parentId: 'tasks',
  sections: [
    createSection('overview', 'Overview', '<p>The story detail page shows the story\'s description, status, and all child tasks. Track overall story progress based on task completion. Add new tasks directly from the story view.</p>'),
  ],
  faqs: [
    createFAQ('faq-1', 'Is a story automatically completed when all tasks are done?', 'The story status may need to be manually updated. Review all tasks are truly complete before marking the story as done.', ['auto', 'complete', 'status']),
  ],
  keywords: ['story', 'epic', 'detail', 'child tasks'],
});
